import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DataTable = ({ data, loading, onSort, sortConfig, onExport }) => {
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const columns = [
    { key: 'date', label: 'Date', sortable: true, width: '120px' },
    { key: 'contract', label: 'Contract', sortable: true, width: '140px' },
    { key: 'exchange', label: 'Exchange', sortable: true, width: '100px' },
    { key: 'open', label: 'Open', sortable: true, width: '100px', align: 'right' },
    { key: 'high', label: 'High', sortable: true, width: '100px', align: 'right' },
    { key: 'low', label: 'Low', sortable: true, width: '100px', align: 'right' },
    { key: 'close', label: 'Close', sortable: true, width: '100px', align: 'right' },
    { key: 'volume', label: 'Volume', sortable: true, width: '120px', align: 'right' },
    { key: 'openInterest', label: 'Open Interest', sortable: true, width: '120px', align: 'right' },
    { key: 'change', label: 'Change', sortable: true, width: '100px', align: 'right' },
    { key: 'changePercent', label: 'Change %', sortable: true, width: '100px', align: 'right' }
  ];

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data?.slice(startIndex, startIndex + itemsPerPage);
  }, [data, currentPage]);

  const totalPages = Math.ceil(data?.length / itemsPerPage);

  const handleSort = (columnKey) => {
    onSort(columnKey);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRows(new Set(paginatedData.map(row => row.id)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (rowId, checked) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected?.add(rowId);
    } else {
      newSelected?.delete(rowId);
    }
    setSelectedRows(newSelected);
  };

  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined) return '-';
    return Number(value)?.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  const formatVolume = (value) => {
    if (value === null || value === undefined) return '-';
    if (value >= 1000000) {
      return `${(value / 1000000)?.toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000)?.toFixed(1)}K`;
    }
    return value?.toLocaleString();
  };

  const getChangeColor = (value) => {
    if (value > 0) return 'text-success';
    if (value < 0) return 'text-error';
    return 'text-foreground';
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig?.key !== columnKey) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-sm">
        <div className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading historical data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm">
      {/* Table Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-foreground">Historical Data</h3>
          <span className="text-sm text-muted-foreground">
            {data?.length?.toLocaleString()} records
          </span>
          {selectedRows?.size > 0 && (
            <span className="text-sm text-primary">
              {selectedRows?.size} selected
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onExport('csv')}
            iconName="Download"
            iconPosition="left"
          >
            Export CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onExport('excel')}
            iconName="FileSpreadsheet"
            iconPosition="left"
          >
            Export Excel
          </Button>
        </div>
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="w-12 p-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedRows?.size === paginatedData?.length && paginatedData?.length > 0}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                  className="rounded border-border"
                />
              </th>
              {columns?.map((column) => (
                <th
                  key={column?.key}
                  className={`p-3 text-${column?.align || 'left'} font-medium text-foreground`}
                  style={{ width: column?.width }}
                >
                  {column?.sortable ? (
                    <button
                      onClick={() => handleSort(column?.key)}
                      className="flex items-center gap-1 hover:text-primary transition-colors"
                    >
                      {column?.label}
                      <Icon name={getSortIcon(column?.key)} size={14} />
                    </button>
                  ) : (
                    column?.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData?.map((row, index) => (
              <tr
                key={row?.id}
                className={`border-b border-border hover:bg-muted/30 transition-colors ${
                  index % 2 === 0 ? 'bg-background' : 'bg-muted/10'
                }`}
              >
                <td className="p-3">
                  <input
                    type="checkbox"
                    checked={selectedRows?.has(row?.id)}
                    onChange={(e) => handleSelectRow(row?.id, e?.target?.checked)}
                    className="rounded border-border"
                  />
                </td>
                <td className="p-3 text-sm text-foreground">{row?.date}</td>
                <td className="p-3 text-sm text-foreground font-medium">{row?.contract}</td>
                <td className="p-3 text-sm text-muted-foreground">{row?.exchange}</td>
                <td className="p-3 text-sm text-right text-foreground">{formatNumber(row?.open)}</td>
                <td className="p-3 text-sm text-right text-foreground">{formatNumber(row?.high)}</td>
                <td className="p-3 text-sm text-right text-foreground">{formatNumber(row?.low)}</td>
                <td className="p-3 text-sm text-right text-foreground font-medium">{formatNumber(row?.close)}</td>
                <td className="p-3 text-sm text-right text-foreground">{formatVolume(row?.volume)}</td>
                <td className="p-3 text-sm text-right text-foreground">{formatVolume(row?.openInterest)}</td>
                <td className={`p-3 text-sm text-right font-medium ${getChangeColor(row?.change)}`}>
                  {row?.change > 0 ? '+' : ''}{formatNumber(row?.change)}
                </td>
                <td className={`p-3 text-sm text-right font-medium ${getChangeColor(row?.changePercent)}`}>
                  {row?.changePercent > 0 ? '+' : ''}{formatNumber(row?.changePercent)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between p-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, data?.length)} of {data?.length} entries
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              iconName="ChevronLeft"
            />
            <span className="text-sm text-foreground px-3">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              iconName="ChevronRight"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;